export type RuntimeCursor = { changed_at: string | null; entity_id: string | null };
export type LocalProjectionDomain = "MEMBER" | "CUSTOMER_CREDIT" | "STOCK" | "STOCK_HISTORY" | "EMPLOYEE_DIRECTORY" | "COMMERCE_CALCULATION" | "LOYALTY";

export type MemberIdentifierSnapshot = {
  identifier_type: string;
  normalized_value: string;
  display_value: string | null;
  is_primary: boolean;
};

export type MemberTierSnapshot = {
  assignment_id: string;
  tier_id: string;
  tier_code: string;
  tier_name: string;
  tier_status: string;
  sort_order: number;
  is_default_tier: boolean;
  assignment_source: string;
  reason: string | null;
  effective_from: string;
  effective_to: string | null;
};

export type MemberRuntimeItem = {
  customer_id: string;
  membership_id: string;
  customer_code: string;
  customer_name: string;
  phone: string | null;
  email: string | null;
  customer_status: string;
  membership_status: string;
  joined_at: string | null;
  active: boolean;
  changed_at: string;
  identifiers: MemberIdentifierSnapshot[];
  tier?: MemberTierSnapshot | null;
};

export type CustomerOpenReceivableRuntimeItem = {
  receivable_id: string;
  receivable_no: string;
  source_order_id: string;
  order_no: string | null;
  branch_id: string;
  branch_code: string | null;
  branch_name: string | null;
  business_date: string | null;
  sale_occurred_at?: string | null;
  created_at: string;
  due_date: string | null;
  currency_code: string;
  original_amount: number;
  allocated_amount?: number;
  remaining_amount?: number;
  receivable_status: "OPEN" | "PARTIALLY_PAID";
};

export type CustomerCreditRuntimeItem = {
  credit_account_id: string;
  customer_id: string;
  status: string;
  credit_limit: number | null;
  currency_code: string;
  payment_term_days: number | null;
  version_no: number;
  branch_scope_mode: "ALL_BRANCHES" | "SELECTED_BRANCHES";
  branch_eligible: boolean;
  eligible: boolean;
  eligibility_reason: string | null;
  current_outstanding: number;
  available_credit: number | null;
  open_receivable_count: number;
  open_receivables?: CustomerOpenReceivableRuntimeItem[];
  changed_at: string;
};

export type StockRuntimeItem = {
  stock_item_id: string;
  item_code: string;
  canonical_name: string;
  normalized_name: string;
  category_code: string | null;
  category_name: string | null;
  stock_group_id?: string | null;
  stock_group_name?: string | null;
  stock_group_sort_order?: number | null;
  stock_group_status?: "ACTIVE" | "INACTIVE" | null;
  item_status: string;
  organization_item_status: string;
  active: boolean;
  sku_code: string | null;
  barcodes: string[];
  quantity_on_hand: number | null;
  unit_id: string | null;
  tracking_basis: string | null;
  state_version: number | null;
  last_movement_at: string | null;
  changed_at: string;
};

export type StockMovementHistoryRuntimeItem = {
  movement_id: string;
  stock_item_id: string;
  direction: "INCREASE" | "DECREASE";
  quantity_delta: number;
  unit_id: string;
  unit_name: string;
  balance_before: number | null;
  balance_after: number | null;
  reason_code: string;
  reason_label: string;
  effect_class: string;
  detail_note: string | null;
  actor_display_name: string | null;
  actor_kind: "SYSTEM" | "USER" | string;
  employee_code?: string | null;
  source_label?: string | null;
  occurred_at: string;
  changed_at: string;
};

export type EmployeeDirectoryRuntimeItem = {
  employee_id: string;
  employee_code: string;
  employee_name: string;
  employment_status: string;
  eligible: boolean;
  changed_at: string;
};

export type CommerceCalculationRuntimeItem = {
  settings_version_id: string;
  version_no: number;
  rounding_scope: "ALL_PAYMENTS";
  rounding_mode: "NONE" | "NEAREST" | "DOWN" | "UP";
  rounding_increment: number;
  effective_from: string | null;
  effective_to: string | null;
  changed_at: string;
};

export type LoyaltyCatalogRuleRuntime = {
  reference_type: "MENU_CATEGORY" | "MENU_ITEM" | "SELLABLE_VARIANT";
  reference_id: string;
  effect: "NO_EARN" | "POINT_OVERRIDE" | "EARN_MULTIPLIER";
  multiplier?: number;
  formula?: {
    type?: string;
    source_value?: number;
    points_value?: number;
    rounding?: "FLOOR" | "ROUND_HALF_UP" | "CEIL";
  };
};

export type LoyaltyBaseEarnPolicyRuntime = {
  schema_version?: number;
  fractional_point_mode?: "ROUND_POLICY" | "PRESERVE_DECIMAL" | string;
  formula: {
    type: "ELIGIBLE_QUANTITY_RATIO" | "CATALOG_WEIGHTED" | "COMMERCE_AMOUNT_RATIO" | "ORDER_COUNT_RATIO" | string;
    source_value: number;
    points_value: number;
    rounding: "FLOOR" | "ROUND_HALF_UP" | "CEIL" | string;
    unit_label?: string | null;
  };
  eligibility?: {
    minimum_unit_amount?: number | null;
    minimum_order_amount?: number | null;
    amount_basis?: "BEFORE_DISCOUNT" | "AFTER_DISCOUNT" | "FINAL_PAYABLE" | string;
    quantity_promotion_treatment?: "INCLUDE_PROMOTION_AFFECTED_UNITS" | "EXCLUDE_PROMOTION_AFFECTED_UNITS" | string;
    promotion_earn_mode?: "USE_PROMOTION_TREATMENT" | "NORMAL_PRICE_ONLY" | string;
  };
  credit_expiry?: {
    mode?: "NONE" | "END_OF_SAME_MONTH_NEXT_YEAR" | string;
  };
  catalog_rules?: LoyaltyCatalogRuleRuntime[];
  presentation?: { point_label?: string };
};

export type LoyaltyRuntimeItem = {
  projection_kind: "BASE_EARN_POLICY";
  loyalty_program_id: string;
  program_status: string;
  policy_version_id: string;
  policy_version_no: number;
  policy_schema_version: number;
  resolved_scope_type: "ORGANIZATION" | "BRANCH" | string;
  resolved_branch_id: string | null;
  policy: LoyaltyBaseEarnPolicyRuntime;
  effective_from: string | null;
  effective_to: string | null;
  local_evaluation_mode: string;
  changed_at: string;
};

export type LoyaltyRewardCatalogRefRuntime = {
  reference_type: "MENU_CATEGORY" | "MENU_ITEM" | "SELLABLE_VARIANT";
  reference_id: string;
  match_mode: "INCLUDE" | "EXCLUDE";
  label?: string | null;
};

export type LoyaltyRewardRuntime = {
  reward_definition_id: string;
  reward_version_id: string;
  version_no: number;
  name: string;
  description?: string | null;
  point_cost: number;
  benefit_type: "FIXED_AMOUNT_DISCOUNT" | "CATALOG_ITEM" | "STOCK_ITEM" | string;
  benefit_document: Record<string, unknown>;
  benefit_reference?: Record<string, unknown> | null;
  featured: boolean;
  priority_rank: number;
  effective_from?: string | null;
  effective_to?: string | null;
  branch_ids: string[];
  channel_ids: string[];
  catalog_refs: LoyaltyRewardCatalogRefRuntime[];
};

export type LoyaltyRewardCatalogRuntimeItem = {
  projection_kind: "REWARD_CATALOG";
  loyalty_program_id: string;
  program_status: string;
  branch_id: string;
  commerce_channel_id: string | null;
  rewards: LoyaltyRewardRuntime[];
  local_evaluation_mode: "PUBLISHED_REWARD_CATALOG" | string;
  changed_at: string;
};

type DomainItemMap = {
  MEMBER: MemberRuntimeItem;
  CUSTOMER_CREDIT: CustomerCreditRuntimeItem;
  STOCK: StockRuntimeItem;
  STOCK_HISTORY: StockMovementHistoryRuntimeItem;
  EMPLOYEE_DIRECTORY: EmployeeDirectoryRuntimeItem;
  COMMERCE_CALCULATION: CommerceCalculationRuntimeItem;
  LOYALTY: LoyaltyRuntimeItem | LoyaltyRewardCatalogRuntimeItem;
};

export type LocalProjectionPayload<D extends LocalProjectionDomain> = {
  ok: boolean;
  contract: string;
  owner: string;
  organization_id: string;
  branch_id?: string;
  mode: "FULL" | "DELTA" | "DISABLED";
  items: DomainItemMap[D][];
  next_cursor: RuntimeCursor | null;
  has_more: boolean;
  server_at: string;
};

type GatewayEnvelope<T> = { ok: boolean; data?: T; error?: string };

export class PosLocalProjectionGatewayClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: { baseUrl: string; timeoutMs?: number }) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 15_000;
  }

  async load<D extends LocalProjectionDomain>(input: {
    deviceSecret: string;
    sessionToken: string;
    domain: D;
    cursor: RuntimeCursor | null;
    limit?: number;
  }): Promise<{ ok: boolean; data: LocalProjectionPayload<D> | null; error: string | null }> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await fetch(`${this.baseUrl}/api/pos/runtime/local-projections`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          device_secret: input.deviceSecret,
          session_token: input.sessionToken,
          domain: input.domain,
          cursor: input.cursor,
          limit: input.limit ?? 500,
        }),
        credentials: "omit",
        signal: controller.signal,
      });
      const payload = (await response.json()) as GatewayEnvelope<LocalProjectionPayload<D>>;
      if (!response.ok || !payload.ok || !payload.data) {
        return { ok: false, data: null, error: payload.error ?? `GATEWAY_HTTP_${response.status}` };
      }
      return { ok: true, data: payload.data, error: null };
    } catch (error) {
      return {
        ok: false,
        data: null,
        error: error instanceof DOMException && error.name === "AbortError" ? "GATEWAY_TIMEOUT" : "GATEWAY_UNREACHABLE",
      };
    } finally {
      window.clearTimeout(timeout);
    }
  }
}
